/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import Container from 'bundles/premium-hub/components/shared/Container';
import RecordingWebinarCard, {
  getYoutubeThumbnailUrl,
} from 'bundles/premium-hub/components/shared/webinars/RecordingWebinarCard';
import UpcomingWebinarCard from 'bundles/premium-hub/components/shared/webinars/UpcomingWebinarCard';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { Degree_WebinarFragment as DegreeWebinar } from 'bundles/premium-hub/queries/__generated__/getDegreeWebinars';

import _t from 'i18n!nls/premium-hub';

type WebinarsCarouselSectionProps = {
  pastWebinars: DegreeWebinar[];
  upcomingWebinars: DegreeWebinar[];
};

const sectionName: SectionName = 'degree_webinars';

const UPCOMING_CARD_DESKTOP_WIDTH = 520;
const UPCOMING_CARD_MOBILE_WIDTH = 300;
const RECORDING_CARD_DESKTOP_WIDTH = 347;
const RECORDING_CARD_MOBILE_WIDTH = 300;

const styles = {
  container: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
  `,
  title: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  row: css`
    &:not(:last-child) {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  carousel: css`
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: 1fr;
    gap: var(--cds-spacing-150);
  `,
  upcomingCarousel: css`
    grid-auto-columns: ${UPCOMING_CARD_DESKTOP_WIDTH}px;

    ${breakpoints.down('xs')} {
      grid-auto-columns: ${UPCOMING_CARD_MOBILE_WIDTH}px;
    }
  `,
  recordingCarousel: css`
    grid-auto-columns: ${RECORDING_CARD_DESKTOP_WIDTH}px;

    ${breakpoints.down('xs')} {
      grid-auto-columns: ${RECORDING_CARD_MOBILE_WIDTH}px;
    }
  `,
};

const MAX_WEBINAR_COUNT = 16;

export const WebinarsCarouselSection = ({ pastWebinars, upcomingWebinars }: WebinarsCarouselSectionProps) => {
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const visibilityTracingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        index: sectionIndex,
        sectionName,
      },
    }
  );

  const upcomingWebinarCarouselItems = upcomingWebinars
    .filter((webinar) => !!webinar.registerLink)
    .map((webinar) => <UpcomingWebinarCard key={webinar.id} webinar={webinar} sectionName={sectionName} />)
    .slice(0, MAX_WEBINAR_COUNT);
  const recordingWebinarCarouselItems = pastWebinars
    .filter((webinar) => !!getYoutubeThumbnailUrl(webinar.recordingLink || ''))
    .map((webinar) => <RecordingWebinarCard key={webinar.id} webinar={webinar} sectionName={sectionName} />)
    .slice(0, MAX_WEBINAR_COUNT);

  if (!upcomingWebinarCarouselItems.length && !recordingWebinarCarouselItems.length) {
    return null;
  }

  return (
    <Container css={styles.container}>
      <div ref={visibilityTracingRef}>
        <Typography2 component="h2" variant="subtitleLarge" css={styles.title}>
          {_t('Upcoming live webinars and learner sessions')}
        </Typography2>
        {upcomingWebinarCarouselItems.length > 0 && (
          <div css={styles.row}>
            <OverflowCarousel css={[styles.carousel, styles.upcomingCarousel]}>
              {upcomingWebinarCarouselItems}
            </OverflowCarousel>
          </div>
        )}
        {recordingWebinarCarouselItems.length > 0 && (
          <div css={styles.row}>
            <OverflowCarousel css={[styles.carousel, styles.recordingCarousel]}>
              {recordingWebinarCarouselItems}
            </OverflowCarousel>
          </div>
        )}
      </div>
    </Container>
  );
};

export default React.memo(WebinarsCarouselSection);
